import { apiFetch } from "../api/client";
import { getExplicitSelectedCompanyId } from "../utils/tenantContext";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import API from "../config";


function formatLabel(value) {
  if (!value) return "Unknown";

  return String(value)
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}


function formatDate(value) {
  if (!value) return "N/A";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
}


function severityClass(value) {
  return `threat-hunt-badge threat-hunt-${String(
    value || "info"
  ).toLowerCase()}`;
}


function statusClass(value) {
  return `threat-hunt-status threat-hunt-status-${String(
    value || "draft"
  )
    .toLowerCase()
    .replaceAll("_", "-")}`;
}


function ThreatHuntingPage() {
  const [summary, setSummary] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [hunts, setHunts] = useState([]);
  const [selectedHunt, setSelectedHunt] =
    useState(null);
  const [selectedRunResults, setSelectedRunResults] =
    useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] =
    useState(false);
  const [runningQueryId, setRunningQueryId] =
    useState(null);
  const [error, setError] = useState("");
  const [preservingResultId, setPreservingResultId] =
    useState(null);
  const [preservedEvidence, setPreservedEvidence] =
    useState({});
  const [selectedEvidenceIds, setSelectedEvidenceIds] =
    useState([]);
  const [showFindingForm, setShowFindingForm] =
    useState(false);
  const [creatingFinding, setCreatingFinding] =
    useState(false);
  const [escalatingFindingId, setEscalatingFindingId] =
    useState(null);
  const [escalatedIncidents, setEscalatedIncidents] =
    useState({});
  const [soarPlaybooks, setSoarPlaybooks] =
    useState([]);
  const [soarActionKey, setSoarActionKey] =
    useState("");
  const [soarAudit, setSoarAudit] =
    useState({});

  const [
    recommendations,
    setRecommendations,
  ] = useState({});

  const [
    recommendationActionKey,
    setRecommendationActionKey,
  ] = useState("");
  const [findingForm, setFindingForm] = useState({
    title: "",
    description: "",
    finding_type: "suspicious-activity",
    severity: "medium",
    confidence: 75,
    mitre_tactic: "",
    mitre_technique: "",
    assigned_to: "George Taylor",
    recommended_actions: "",
  });
  const [showCreateHunt, setShowCreateHunt] =
    useState(false);
  const [creatingHunt, setCreatingHunt] =
    useState(false);
  const [huntForm, setHuntForm] = useState({
    hunt_name: "",
    hypothesis: "",
    description: "",
    objective: "",
    priority: "P3",
    severity: "medium",
    assigned_analyst: "George Taylor",
    assigned_team: "Wireless Press SOC",
  });

  const loadWorkspace = useCallback(
    async (silent = false) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError("");

        const [
          summaryResponse,
          metricsResponse,
          huntsResponse,
          playbooksResponse,
        ] = await Promise.all([
          apiFetch(`/threat-hunting/summary`),
          apiFetch(`/threat-hunting/metrics`),
          apiFetch(`/threat-hunting/hunts?limit=100`
          ),
          apiFetch(`/soar/playbooks`),
        ]);

        const summaryData =
          await summaryResponse.json();

        const metricsData =
          await metricsResponse.json();

        const huntsData =
          await huntsResponse.json();

        const playbooksData =
          await playbooksResponse.json();

        if (!summaryResponse.ok) {
          throw new Error(
            summaryData.detail ||
              "Unable to load threat-hunting summary."
          );
        }

        if (!metricsResponse.ok) {
          throw new Error(
            metricsData.detail ||
              "Unable to load threat-hunting metrics."
          );
        }

        if (!huntsResponse.ok) {
          throw new Error(
            huntsData.detail ||
              "Unable to load threat hunts."
          );
        }

        if (!playbooksResponse.ok) {
          throw new Error(
            playbooksData.detail ||
              "Unable to load SOAR playbooks."
          );
        }

        setSummary(summaryData);
        setMetrics(metricsData);
        setHunts(
          Array.isArray(huntsData)
            ? huntsData
            : []
        );

        setSoarPlaybooks(
          Array.isArray(playbooksData)
            ? playbooksData.filter(
                (playbook) =>
                  playbook.enabled !== false
              )
            : []
        );
      } catch (loadError) {
        console.error(
          "Threat Hunting load error:",
          loadError
        );

        setError(loadError.message);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const createHunt = async (event) => {
    event.preventDefault();

    const companyId = getExplicitSelectedCompanyId();

    if (!companyId) {
      setError(
        "Select a tenant before creating a threat hunt."
      );
      return;
    }

    try {
      setCreatingHunt(true);

      const response = await apiFetch(`/threat-hunting/hunts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...huntForm,
            company_id: Number(companyId),
            status: "active",
            hunt_type: "hypothesis-driven",
            created_by:
              localStorage.getItem("username") ||
              "George Taylor",
            created_by_role: "Threat Hunter",
            data_sources: [
              "identity_authentication_events",
              "ueba_anomalies",
              "normalized_events",
              "zero_trust_access_decisions",
              "incidents",
            ],
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to create threat hunt."
        );
      }

      setShowCreateHunt(false);

      setHuntForm({
        hunt_name: "",
        hypothesis: "",
        description: "",
        objective: "",
        priority: "P3",
        severity: "medium",
        assigned_analyst: "George Taylor",
        assigned_team: "Wireless Press SOC",
      });

      await loadWorkspace(true);
      await openHunt(data.id);
    } catch (createError) {
      console.error(
        "Threat hunt creation error:",
        createError
      );

      window.alert(createError.message);
    } finally {
      setCreatingHunt(false);
    }
  };

  const openHunt = async (huntId) => {
    try {
      const response = await apiFetch(`/threat-hunting/hunts/${huntId}`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to load threat hunt."
        );
      }

      setSelectedHunt(data);
      setSelectedRunResults([]);
    } catch (huntError) {
      console.error(
        "Threat hunt detail error:",
        huntError
      );

      window.alert(huntError.message);
    }
  };

  const executeQuery = async (query) => {
    try {
      setRunningQueryId(query.id);

      const response = await apiFetch(`/threat-hunting/queries/${query.id}/execute`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            executed_by:
              localStorage.getItem("username") ||
              "George Taylor",
            executed_by_role:
              "Threat Hunter",
            parameters:
              query.parameters || {},
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to execute threat-hunt query."
        );
      }

      setSelectedRunResults(
        Array.isArray(data.results)
          ? data.results
          : []
      );

      await loadWorkspace(true);

      if (selectedHunt) {
        await openHunt(selectedHunt.id);
      }
    } catch (executionError) {
      console.error(
        "Threat hunt execution error:",
        executionError
      );

      window.alert(executionError.message);
    } finally {
      setRunningQueryId(null);
    }
  };

  const preserveEvidence = async (result) => {
    try {
      setPreservingResultId(result.id);

      const response = await apiFetch(`/threat-hunting/results/${result.id}/evidence`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            evidence_type:
              result.source_type === "ueba"
                ? "ueba-anomaly"
                : "hunt-result",
            evidence_title:
              `${formatLabel(result.event_type)} Evidence`,
            description:
              "Evidence preserved from the Wireless Press Threat Hunting Workspace.",
            collected_by:
              localStorage.getItem("username") ||
              "George Taylor",
            collector_role: "Threat Hunter",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to preserve threat-hunt evidence."
        );
      }

      setPreservedEvidence((current) => ({
        ...current,
        [result.id]: data,
      }));

      if (selectedHunt) {
        await openHunt(selectedHunt.id);
      }
    } catch (preserveError) {
      console.error(
        "Evidence preservation error:",
        preserveError
      );

      window.alert(preserveError.message);
    } finally {
      setPreservingResultId(null);
    }
  };

  const toggleEvidenceSelection = (
    evidenceId
  ) => {
    setSelectedEvidenceIds((current) =>
      current.includes(evidenceId)
        ? current.filter(
            (item) => item !== evidenceId
          )
        : [...current, evidenceId]
    );
  };

  const createFinding = async (event) => {
    event.preventDefault();

    if (!selectedHunt) {
      window.alert("Open a threat hunt first.");
      return;
    }

    if (selectedEvidenceIds.length === 0) {
      window.alert(
        "Select at least one preserved evidence item."
      );
      return;
    }

    try {
      setCreatingFinding(true);

      const recommendedActions =
        findingForm.recommended_actions
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean);

      const response = await apiFetch(`/threat-hunting/hunts/${selectedHunt.id}/findings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            title: findingForm.title,
            description: findingForm.description,
            finding_type:
              findingForm.finding_type,
            severity: findingForm.severity,
            confidence: Number(
              findingForm.confidence
            ),
            mitre_tactic:
              findingForm.mitre_tactic || null,
            mitre_technique:
              findingForm.mitre_technique || null,
            affected_entities: [
              selectedHunt.assigned_analyst ||
                "Unknown",
            ],
            evidence_ids:
              selectedEvidenceIds,
            recommended_actions:
              recommendedActions,
            assigned_to:
              findingForm.assigned_to,
            created_by:
              localStorage.getItem("username") ||
              "George Taylor",
            created_by_role: "Threat Hunter",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to create threat-hunt finding."
        );
      }

      setShowFindingForm(false);
      setSelectedEvidenceIds([]);

      setFindingForm({
        title: "",
        description: "",
        finding_type: "suspicious-activity",
        severity: "medium",
        confidence: 75,
        mitre_tactic: "",
        mitre_technique: "",
        assigned_to: "George Taylor",
        recommended_actions: "",
      });

      await loadWorkspace(true);
      await openHunt(selectedHunt.id);

      window.alert(
        `Finding ${data.finding_number} created.`
      );
    } catch (findingError) {
      console.error(
        "Threat finding creation error:",
        findingError
      );

      window.alert(findingError.message);
    } finally {
      setCreatingFinding(false);
    }
  };

  const escalateFinding = async (finding) => {
    try {
      setEscalatingFindingId(finding.id);

      const response = await apiFetch(`/threat-hunting/findings/${finding.id}/escalate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            assigned_team: "Wireless Press SOC",
            created_by:
              localStorage.getItem("username") ||
              "George Taylor",
            created_by_role: "Threat Hunter",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to escalate finding to SOC."
        );
      }

      const incident =
        data.incident || {};

      setEscalatedIncidents((current) => ({
        ...current,
        [finding.id]: incident,
      }));

      try {
        const recommendationResponse = await apiFetch(`/soar/recommend`,
          {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json",
            },
            body: JSON.stringify({
              incident_id: incident.id,
            }),
          }
        );

        const recommendationData =
          await recommendationResponse.json();

        if (!recommendationResponse.ok) {
          throw new Error(
            recommendationData.detail ||
              "Unable to generate SOAR recommendation."
          );
        }

        const recommendationItems =
          Array.isArray(
            recommendationData.recommendations
          )
            ? recommendationData.recommendations
            : [];

        setRecommendations((current) => ({
          ...current,
          [incident.id]:
            recommendationItems,
        }));
      } catch (recommendationError) {
        console.error(
          "Automatic SOAR recommendation error:",
          recommendationError
        );
      }

      if (selectedHunt) {
        await loadWorkspace(true);
        await openHunt(selectedHunt.id);
      }

      window.alert(
        incident.incident_number
          ? `Escalated to ${incident.incident_number}.`
          : "Finding escalated to SOC."
      );
    } catch (escalationError) {
      console.error(
        "Threat finding escalation error:",
        escalationError
      );

      window.alert(
        escalationError.message
      );
    } finally {
      setEscalatingFindingId(null);
    }
  };

  const getFindingSoarRun = (findingId) => {
    return (
      selectedHunt?.soar_runs || []
    ).find(
      (run) => run.finding_id === findingId
    );
  };

  const loadRecommendations = async (
    incidentId
  ) => {
    if (!incidentId) {
      return [];
    }

    try {
      const response = await apiFetch(`/soar/incidents/${incidentId}/recommendations`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to load SOAR recommendations."
        );
      }

      const items = Array.isArray(data)
        ? data
        : data.recommendations || [];

      setRecommendations((current) => ({
        ...current,
        [incidentId]: items,
      }));

      return items;
    } catch (recommendationError) {
      console.error(
        "SOAR recommendation load error:",
        recommendationError
      );

      return [];
    }
  };

  const generateRecommendation = async (
    finding
  ) => {
    if (!finding.incident_id) {
      window.alert(
        "Escalate the finding to a SOC incident first."
      );
      return;
    }

    const actionKey =
      `recommend-${finding.id}`;

    try {
      setRecommendationActionKey(
        actionKey
      );

      const response = await apiFetch(`/soar/recommend`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            incident_id:
              finding.incident_id,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to generate a SOAR recommendation."
        );
      }

      const items =
        Array.isArray(data.recommendations)
          ? data.recommendations
          : [];

      setRecommendations((current) => ({
        ...current,
        [finding.incident_id]: items,
      }));

      if (items.length === 0) {
        window.alert(
          "No playbook met the recommendation threshold."
        );
      }
    } catch (recommendationError) {
      console.error(
        "SOAR recommendation generation error:",
        recommendationError
      );

      window.alert(
        recommendationError.message
      );
    } finally {
      setRecommendationActionKey("");
    }
  };

  const runRecommendedPlaybook = async (
    finding,
    recommendation
  ) => {
    if (!recommendation?.id) {
      window.alert(
        "No recommendation is available."
      );
      return;
    }

    const actionKey =
      `run-recommendation-${finding.id}`;

    try {
      setRecommendationActionKey(
        actionKey
      );

      const username =
        localStorage.getItem("username") ||
        "George Taylor";

      const runResponse = await apiFetch(`/soar/recommendations/${recommendation.id}/run`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            requested_by: username,
            requested_by_role:
              "Threat Hunter",
          }),
        }
      );

      const runData =
        await runResponse.json();

      if (!runResponse.ok) {
        throw new Error(
          runData.detail ||
            "Unable to run the recommended playbook."
        );
      }

      const runId =
        runData.run?.id ||
        runData.id;

      if (!runId) {
        throw new Error(
          "The recommendation created no SOAR run ID."
        );
      }

      const linkResponse = await apiFetch(`/threat-hunting/findings/${finding.id}/soar-runs/${runId}/link`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            requested_by: username,
            requested_by_role:
              "Threat Hunter",
          }),
        }
      );

      const linkData =
        await linkResponse.json();

      if (!linkResponse.ok) {
        throw new Error(
          linkData.detail ||
            "The SOAR run was created but could not be linked to the finding."
        );
      }

      await loadWorkspace(true);

      if (selectedHunt?.id) {
        await openHunt(
          selectedHunt.id
        );
      }

      window.alert(
        `Recommended SOAR run ${runId} created and linked.`
      );
    } catch (runError) {
      console.error(
        "Recommended SOAR run error:",
        runError
      );

      window.alert(
        runError.message
      );
    } finally {
      setRecommendationActionKey("");
    }
  };

  const dismissRecommendation = async (
    finding,
    recommendation
  ) => {
    if (!recommendation?.id) {
      return;
    }

    const actionKey =
      `dismiss-recommendation-${finding.id}`;

    try {
      setRecommendationActionKey(
        actionKey
      );

      const response = await apiFetch(`/soar/recommendations/${recommendation.id}/dismiss`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            dismissed_by:
              localStorage.getItem(
                "username"
              ) ||
              "George Taylor",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to dismiss the recommendation."
        );
      }

      await loadRecommendations(
        finding.incident_id
      );
    } catch (dismissError) {
      console.error(
        "Recommendation dismissal error:",
        dismissError
      );

      window.alert(
        dismissError.message
      );
    } finally {
      setRecommendationActionKey("");
    }
  };

  useEffect(() => {
    const findings =
      selectedHunt?.findings || [];

    findings.forEach((finding) => {
      if (
        finding.incident_id &&
        !recommendations[
          finding.incident_id
        ]
      ) {
        loadRecommendations(
          finding.incident_id
        );
      }
    });
  }, [selectedHunt]);

  const runSoarPlaybook = async (
    finding,
    playbookId
  ) => {
    if (!finding.incident_id) {
      window.alert(
        "Escalate the finding to a SOC incident first."
      );
      return;
    }

    const actionKey =
      `create-${finding.id}`;

    try {
      setSoarActionKey(actionKey);

      const runResponse = await apiFetch(`/soar/runs`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            playbook_id: Number(playbookId),
            incident_id:
              finding.incident_id,
            requested_by:
              localStorage.getItem("username") ||
              "George Taylor",
            requested_by_role:
              "Threat Hunter",
          }),
        }
      );

      const runData =
        await runResponse.json();

      if (!runResponse.ok) {
        throw new Error(
          runData.detail ||
            "Unable to create SOAR run."
        );
      }

      const linkResponse = await apiFetch(`/threat-hunting/findings/${finding.id}/soar-runs/${runData.id}/link`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            requested_by:
              localStorage.getItem("username") ||
              "George Taylor",
            requested_by_role:
              "Threat Hunter",
          }),
        }
      );

      const linkData =
        await linkResponse.json();

      if (!linkResponse.ok) {
        throw new Error(
          linkData.detail ||
            "SOAR run was created but could not be linked."
        );
      }

      await loadWorkspace(true);
      await openHunt(selectedHunt.id);

      window.alert(
        `SOAR run ${runData.id} created and linked.`
      );
    } catch (soarError) {
      console.error(
        "SOAR run creation error:",
        soarError
      );

      window.alert(soarError.message);
    } finally {
      setSoarActionKey("");
    }
  };

  const performSoarAction = async (
    run,
    action
  ) => {
    const actionKey =
      `${action}-${run.soar_run_id}`;

    try {
      setSoarActionKey(actionKey);

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      };

      if (action === "approve") {
        requestOptions.body = JSON.stringify({
          approved_by:
            localStorage.getItem("username") ||
            "George Taylor",
          approved_by_role:
            "SOC Manager",
          approval_notes:
            "Approved through the Threat Hunting Workspace.",
        });
      }

      if (action === "reject") {
        requestOptions.body = JSON.stringify({
          rejected_by:
            localStorage.getItem("username") ||
            "George Taylor",
          rejected_by_role:
            "SOC Manager",
          rejection_reason:
            "Rejected through the Threat Hunting Workspace.",
        });
      }

      const response = await apiFetch(`/soar/runs/${run.soar_run_id}/${action}`,
        requestOptions
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            `Unable to ${action} SOAR run.`
        );
      }

      await openHunt(selectedHunt.id);
    } catch (actionError) {
      console.error(
        `SOAR ${action} error:`,
        actionError
      );

      window.alert(actionError.message);
    } finally {
      setSoarActionKey("");
    }
  };

  const viewSoarAudit = async (run) => {
    const actionKey =
      `audit-${run.soar_run_id}`;

    try {
      setSoarActionKey(actionKey);

      const response = await apiFetch(`/soar/runs/${run.soar_run_id}/audit`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to load SOAR audit history."
        );
      }

      setSoarAudit((current) => ({
        ...current,
        [run.soar_run_id]:
          Array.isArray(data)
            ? data
            : data.audit || [],
      }));
    } catch (auditError) {
      console.error(
        "SOAR audit error:",
        auditError
      );

      window.alert(auditError.message);
    } finally {
      setSoarActionKey("");
    }
  };

  const reviewResult = async (
    resultId,
    disposition
  ) => {
    try {
      const response = await apiFetch(`/threat-hunting/results/${resultId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            analyst_disposition: disposition,
            analyst_notes:
              "Reviewed in the Wireless Press Threat Hunting Workspace.",
            reviewed_by:
              localStorage.getItem("username") ||
              "George Taylor",
            reviewed_by_role:
              "Threat Hunter",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to update hunt result."
        );
      }

      setSelectedRunResults((current) =>
        current.map((item) =>
          item.id === resultId
            ? data
            : item
        )
      );
    } catch (reviewError) {
      console.error(
        "Threat hunt review error:",
        reviewError
      );

      window.alert(reviewError.message);
    }
  };

  const huntMetrics =
    summary?.hunt_metrics || {};

  const queryMetrics =
    summary?.query_metrics || {};

  const findingMetrics =
    summary?.finding_metrics || {};

  const recentHunts = useMemo(
    () =>
      Array.isArray(summary?.recent_hunts)
        ? summary.recent_hunts
        : [],
    [summary]
  );

  const mitreCoverage = useMemo(() => {
    if (!selectedHunt) {
      return {
        tactics: [],
        techniques: [],
      };
    }

    const tacticValues = [
      ...(selectedHunt.mitre_tactics || []),
      ...(selectedHunt.findings || [])
        .map((finding) => finding.mitre_tactic)
        .filter(Boolean),
    ];

    const techniqueValues = [
      ...(selectedHunt.mitre_techniques || []),
      ...(selectedHunt.findings || [])
        .map((finding) => finding.mitre_technique)
        .filter(Boolean),
    ];

    return {
      tactics: [...new Set(tacticValues)],
      techniques: [...new Set(techniqueValues)],
    };
  }, [selectedHunt]);

  const investigationNodes = useMemo(() => {
    if (!selectedHunt) return [];

    const nodes = [];

    const result =
      selectedRunResults[0] || null;

    const evidence =
      (selectedHunt.evidence || [])[0] || null;

    const finding =
      (selectedHunt.findings || [])[0] || null;

    if (result?.username) {
      nodes.push({
        key: "identity",
        type: "Identity",
        label: result.username,
      });
    }

    if (result) {
      nodes.push({
        key: "event",
        type: "Event",
        label: formatLabel(
          result.event_type
        ),
      });
    }

    if (evidence) {
      nodes.push({
        key: "evidence",
        type: "Evidence",
        label:
          evidence.evidence_title ||
          `Evidence #${evidence.id}`,
      });
    }

    if (finding) {
      nodes.push({
        key: "finding",
        type: "Finding",
        label: finding.title,
      });
    }

    const incidentId =
      finding?.incident_id ||
      selectedHunt.incident_id;

    if (incidentId) {
      nodes.push({
        key: "incident",
        type: "SOC Incident",
        label:
          escalatedIncidents[
            finding?.id
          ]?.incident_number ||
          `Incident #${incidentId}`,
      });
    }

    return nodes;
  }, [
    selectedHunt,
    selectedRunResults,
    escalatedIncidents,
  ]);

  if (loading) {
    return (
      <div className="threat-hunt-loading">
        Loading Threat Hunting Workspace...
      </div>
    );
  }

  return (
    <div className="threat-hunt-page">
      <section className="threat-hunt-header">
        <div>
          <div className="threat-hunt-eyebrow">
            PROACTIVE THREAT INVESTIGATION
          </div>

          <h2>Threat Hunting Workspace</h2>

          <p>
            Run hypothesis-driven investigations,
            review behavioral and endpoint evidence,
            preserve findings, and escalate confirmed
            activity into SOC incidents.
          </p>
        </div>

        <div className="threat-hunt-header-actions">
          <button
            type="button"
            onClick={() =>
              setShowCreateHunt(true)
            }
          >
            Create Hunt
          </button>

          <button
            type="button"
            onClick={() => loadWorkspace(true)}
            disabled={refreshing}
          >
            {refreshing
              ? "Refreshing..."
              : "Refresh Workspace"}
          </button>
        </div>
      </section>

      {error && (
        <div className="threat-hunt-warning">
          Threat Hunting refresh failed: {error}
        </div>
      )}

      {showCreateHunt && (
        <section className="threat-hunt-panel">
          <div className="threat-hunt-panel-heading threat-hunt-detail-heading">
            <div>
              <span>NEW INVESTIGATION</span>
              <h3>Create Threat Hunt</h3>
            </div>

            <button
              type="button"
              className="threat-hunt-close-button"
              onClick={() =>
                setShowCreateHunt(false)
              }
            >
              Cancel
            </button>
          </div>

          <form
            className="threat-hunt-create-form"
            onSubmit={createHunt}
          >
            <label>
              Hunt Name
              <input
                required
                value={huntForm.hunt_name}
                onChange={(event) =>
                  setHuntForm({
                    ...huntForm,
                    hunt_name:
                      event.target.value,
                  })
                }
              />
            </label>

            <label className="threat-hunt-form-wide">
              Hypothesis
              <textarea
                required
                value={huntForm.hypothesis}
                onChange={(event) =>
                  setHuntForm({
                    ...huntForm,
                    hypothesis:
                      event.target.value,
                  })
                }
              />
            </label>

            <label className="threat-hunt-form-wide">
              Description
              <textarea
                value={huntForm.description}
                onChange={(event) =>
                  setHuntForm({
                    ...huntForm,
                    description:
                      event.target.value,
                  })
                }
              />
            </label>

            <label>
              Priority
              <select
                value={huntForm.priority}
                onChange={(event) =>
                  setHuntForm({
                    ...huntForm,
                    priority:
                      event.target.value,
                  })
                }
              >
                <option value="P1">P1</option>
                <option value="P2">P2</option>
                <option value="P3">P3</option>
                <option value="P4">P4</option>
              </select>
            </label>

            <label>
              Severity
              <select
                value={huntForm.severity}
                onChange={(event) =>
                  setHuntForm({
                    ...huntForm,
                    severity:
                      event.target.value,
                  })
                }
              >
                <option value="critical">
                  Critical
                </option>
                <option value="high">
                  High
                </option>
                <option value="medium">
                  Medium
                </option>
                <option value="low">
                  Low
                </option>
              </select>
            </label>

            <label>
              Assigned Analyst
              <input
                value={
                  huntForm.assigned_analyst
                }
                onChange={(event) =>
                  setHuntForm({
                    ...huntForm,
                    assigned_analyst:
                      event.target.value,
                  })
                }
              />
            </label>

            <label>
              Assigned Team
              <input
                value={huntForm.assigned_team}
                onChange={(event) =>
                  setHuntForm({
                    ...huntForm,
                    assigned_team:
                      event.target.value,
                  })
                }
              />
            </label>

            <div className="threat-hunt-form-actions threat-hunt-form-wide">
              <button
                type="submit"
                disabled={creatingHunt}
              >
                {creatingHunt
                  ? "Creating..."
                  : "Create Active Hunt"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="threat-hunt-metrics">
        <article>
          <span>Total Hunts</span>
          <strong>
            {huntMetrics.total_hunts || 0}
          </strong>
        </article>

        <article>
          <span>Active Hunts</span>
          <strong>
            {huntMetrics.active_hunts || 0}
          </strong>
        </article>

        <article>
          <span>Completed Hunts</span>
          <strong>
            {huntMetrics.completed_hunts || 0}
          </strong>
        </article>

        <article>
          <span>Query Runs</span>
          <strong>
            {queryMetrics.total_query_runs || 0}
          </strong>
        </article>

        <article>
          <span>Total Matches</span>
          <strong>
            {queryMetrics.total_matches || 0}
          </strong>
        </article>

        <article>
          <span>Open Findings</span>
          <strong>
            {findingMetrics.open_findings || 0}
          </strong>
        </article>

        <article className="threat-hunt-critical-card">
          <span>High-Priority Findings</span>
          <strong>
            {findingMetrics.high_priority_findings ||
              0}
          </strong>
        </article>

        <article>
          <span>Escalated Findings</span>
          <strong>
            {findingMetrics.escalated_findings ||
              0}
          </strong>
        </article>
      </section>

      <section className="threat-hunt-kpi-grid">
        <article>
          <span>Hunt Success Rate</span>
          <strong>
            {Number(
              metrics?.rates?.hunt_success_rate || 0
            ).toFixed(1)}
            %
          </strong>
        </article>

        <article>
          <span>Escalation Rate</span>
          <strong>
            {Number(
              metrics?.rates?.escalation_rate || 0
            ).toFixed(1)}
            %
          </strong>
        </article>

        <article>
          <span>Reviewed Result Rate</span>
          <strong>
            {Number(
              metrics?.rates?.reviewed_result_rate ||
                0
            ).toFixed(1)}
            %
          </strong>
        </article>

        <article>
          <span>Evidence Collected</span>
          <strong>
            {metrics?.evidence_metrics
              ?.total_evidence || 0}
          </strong>
        </article>

        <article>
          <span>Average Confidence</span>
          <strong>
            {Number(
              metrics?.finding_metrics
                ?.average_confidence || 0
            ).toFixed(1)}
            %
          </strong>
        </article>

        <article>
          <span>Average Query Time</span>
          <strong>
            {Number(
              metrics?.query_metrics
                ?.average_execution_time_ms || 0
            ).toFixed(0)}
            <small> ms</small>
          </strong>
        </article>

        <article>
          <span>Average Investigation</span>
          <strong>
            {Number(
              metrics?.hunt_metrics
                ?.average_investigation_minutes || 0
            ).toFixed(1)}
            <small> min</small>
          </strong>
        </article>

        <article>
          <span>Findings per Completed Hunt</span>
          <strong>
            {Number(
              metrics?.rates
                ?.findings_per_completed_hunt || 0
            ).toFixed(1)}
          </strong>
        </article>
      </section>

      <section className="threat-hunt-panel">
        <div className="threat-hunt-panel-heading">
          <div>
            <span>INVESTIGATION INVENTORY</span>
            <h3>Threat Hunts</h3>
          </div>
        </div>

        <div className="threat-hunt-table-wrapper">
          <table className="threat-hunt-table">
            <thead>
              <tr>
                <th>Hunt</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Severity</th>
                <th>Analyst</th>
                <th>Queries</th>
                <th>Results</th>
                <th>Findings</th>
                <th>Updated</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {hunts.length > 0 ? (
                hunts.map((hunt) => (
                  <tr key={hunt.id}>
                    <td>
                      <strong>
                        {hunt.hunt_name}
                      </strong>

                      <span className="threat-hunt-subtext">
                        {hunt.hunt_number}
                      </span>
                    </td>

                    <td>
                      <span
                        className={statusClass(
                          hunt.status
                        )}
                      >
                        {formatLabel(hunt.status)}
                      </span>
                    </td>

                    <td>{hunt.priority}</td>

                    <td>
                      <span
                        className={severityClass(
                          hunt.severity
                        )}
                      >
                        {formatLabel(
                          hunt.severity
                        )}
                      </span>
                    </td>

                    <td>
                      {hunt.assigned_analyst ||
                        "Unassigned"}
                    </td>

                    <td>
                      {hunt.query_count || 0}
                    </td>

                    <td>
                      {hunt.result_count || 0}
                    </td>

                    <td>
                      {hunt.finding_count || 0}
                    </td>

                    <td>
                      {formatDate(
                        hunt.updated_at
                      )}
                    </td>

                    <td>
                      <button
                        type="button"
                        className="threat-hunt-row-button"
                        onClick={() =>
                          openHunt(hunt.id)
                        }
                      >
                        Open Hunt
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="10"
                    className="threat-hunt-empty"
                  >
                    No threat hunts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="threat-hunt-grid">
        <article className="threat-hunt-panel">
          <div className="threat-hunt-panel-heading">
            <div>
              <span>RECENT ACTIVITY</span>
              <h3>Recent Hunts</h3>
            </div>
          </div>

          <div className="threat-hunt-card-list">
            {recentHunts.map((hunt) => (
              <button
                type="button"
                key={hunt.id}
                onClick={() =>
                  openHunt(hunt.id)
                }
              >
                <div>
                  <strong>
                    {hunt.hunt_name}
                  </strong>

                  <span>
                    {hunt.hunt_number}
                  </span>
                </div>

                <div>
                  <span>
                    {hunt.result_count || 0}
                    {" "}results
                  </span>

                  <span>
                    {hunt.finding_count || 0}
                    {" "}findings
                  </span>
                </div>
              </button>
            ))}
          </div>
        </article>

        <article className="threat-hunt-panel">
          <div className="threat-hunt-panel-heading">
            <div>
              <span>SAFE QUERY ENGINE</span>
              <h3>Approved Data Sources</h3>
            </div>
          </div>

          <div className="threat-hunt-dataset-grid">
            {(summary?.allowed_datasets || []).map(
              (dataset) => (
                <div key={dataset}>
                  {formatLabel(dataset)}
                </div>
              )
            )}
          </div>
        </article>
      </section>

      {selectedHunt && (
        <section className="threat-hunt-panel">
          <div className="threat-hunt-panel-heading threat-hunt-detail-heading">
            <div>
              <span>ACTIVE INVESTIGATION</span>

              <h3>
                {selectedHunt.hunt_name}
              </h3>

              <p>
                {selectedHunt.hypothesis}
              </p>
            </div>

            <button
              type="button"
              className="threat-hunt-close-button"
              onClick={() => {
                setSelectedHunt(null);
                setSelectedRunResults([]);
              }}
            >
              Close
            </button>
          </div>

          <div className="threat-hunt-detail-metrics">
            <div>
              <span>Status</span>
              <strong>
                {formatLabel(
                  selectedHunt.status
                )}
              </strong>
            </div>

            <div>
              <span>Priority</span>
              <strong>
                {selectedHunt.priority}
              </strong>
            </div>

            <div>
              <span>Severity</span>
              <strong>
                {formatLabel(
                  selectedHunt.severity
                )}
              </strong>
            </div>

            <div>
              <span>Assigned Analyst</span>
              <strong>
                {selectedHunt.assigned_analyst ||
                  "Unassigned"}
              </strong>
            </div>
          </div>

          <div className="threat-hunt-detail-grid">
            <article>
              <h4>Saved Queries</h4>

              {(selectedHunt.queries || []).map(
                (query) => (
                  <div
                    className="threat-hunt-query-card"
                    key={query.id}
                  >
                    <div>
                      <strong>
                        {query.query_name}
                      </strong>

                      <span>
                        {formatLabel(
                          query.target_dataset
                        )}
                      </span>
                    </div>

                    <button
                      type="button"
                      disabled={
                        runningQueryId === query.id
                      }
                      onClick={() =>
                        executeQuery(query)
                      }
                    >
                      {runningQueryId === query.id
                        ? "Running..."
                        : "Run Query"}
                    </button>
                  </div>
                )
              )}
            </article>

            <article>
              <h4>Findings</h4>

              {(selectedHunt.findings || [])
                .length > 0 ? (
                selectedHunt.findings.map(
                  (finding) => (
                    <div
                      className="threat-hunt-finding-card"
                      key={finding.id}
                    >
                      <div className="threat-hunt-finding-main">
                        <div>
                          <strong>
                            {finding.title}
                          </strong>

                          <span>
                            {finding.finding_number}
                          </span>
                        </div>

                        <div className="threat-hunt-finding-meta">
                          <span
                            className={severityClass(
                              finding.severity
                            )}
                          >
                            {formatLabel(
                              finding.severity
                            )}
                          </span>

                          <small>
                            {finding.confidence}%
                            {" "}confidence
                          </small>
                        </div>
                      </div>

                      <div className="threat-hunt-finding-actions">
                        {finding.incident_id ||
                        escalatedIncidents[finding.id] ? (
                          <>
                            <div className="threat-hunt-incident-confirmed">
                              <strong>
                                {escalatedIncidents[
                                  finding.id
                                ]?.incident_number ||
                                  `Incident #${finding.incident_id}`}
                              </strong>

                              <span>
                                Escalated to SOC
                              </span>
                            </div>

                            {getFindingSoarRun(
                              finding.id
                            ) ? (
                              <div className="threat-hunt-existing-run-recommendation">
                                {(() => {
                                  const incidentRecommendations =
                                    recommendations[
                                      finding.incident_id
                                    ] || [];

                                  const recommendation =
                                    incidentRecommendations.find(
                                      (item) => !item.dismissed
                                    );

                                  if (!recommendation) {
                                    return (
                                      <button
                                        type="button"
                                        className="threat-hunt-recommend-button"
                                        disabled={
                                          recommendationActionKey ===
                                          `recommend-${finding.id}`
                                        }
                                        onClick={() =>
                                          generateRecommendation(
                                            finding
                                          )
                                        }
                                      >
                                        {recommendationActionKey ===
                                        `recommend-${finding.id}`
                                          ? "Analyzing..."
                                          : "Generate Recommendation"}
                                      </button>
                                    );
                                  }

                                  return (
                                    <div className="threat-hunt-recommendation-card">
                                      <div className="threat-hunt-recommendation-header">
                                        <div>
                                          <span>
                                            Recommended Playbook
                                          </span>

                                          <strong>
                                            {
                                              recommendation.playbook_name
                                            }
                                          </strong>
                                        </div>

                                        <div className="threat-hunt-recommendation-confidence">
                                          <strong>
                                            {
                                              recommendation.confidence
                                            }
                                            %
                                          </strong>

                                          <span>
                                            Confidence
                                          </span>
                                        </div>
                                      </div>

                                      <div className="threat-hunt-recommendation-meta">
                                        <span>
                                          Rank #
                                          {
                                            recommendation.recommendation_rank
                                          }
                                        </span>

                                        <span>
                                          Score{" "}
                                          {
                                            recommendation.score
                                          }
                                        </span>

                                        <span>
                                          {
                                            recommendation.rule_name ||
                                            "Recommendation Rule"
                                          }
                                        </span>
                                      </div>

                                      {Array.isArray(
                                        recommendation.recommendation_reasons
                                      ) &&
                                      recommendation.recommendation_reasons
                                        .length > 0 ? (
                                        <div className="threat-hunt-recommendation-reasons">
                                          <span>
                                            Matched signals
                                          </span>

                                          <div>
                                            {recommendation.recommendation_reasons.map(
                                              (
                                                reason,
                                                reasonIndex
                                              ) => (
                                                <small
                                                  key={
                                                    reasonIndex
                                                  }
                                                >
                                                  {
                                                    reason.field
                                                  }
                                                  :{" "}
                                                  {
                                                    reason.actual
                                                  }
                                                  {" +"}
                                                  {
                                                    reason.weight
                                                  }
                                                </small>
                                              )
                                            )}
                                          </div>
                                        </div>
                                      ) : null}

                                      <div className="threat-hunt-recommendation-buttons">
                                        <span className="threat-hunt-recommendation-run-exists">
                                          SOAR run already linked
                                        </span>

                                        <button
                                          type="button"
                                          className="threat-hunt-recommend-button"
                                          disabled={
                                            recommendationActionKey ===
                                            `recommend-${finding.id}`
                                          }
                                          onClick={() =>
                                            generateRecommendation(
                                              finding
                                            )
                                          }
                                        >
                                          {recommendationActionKey ===
                                          `recommend-${finding.id}`
                                            ? "Analyzing..."
                                            : "Refresh Recommendation"}
                                        </button>

                                        <button
                                          type="button"
                                          className="threat-hunt-recommendation-dismiss-button"
                                          disabled={
                                            recommendationActionKey ===
                                            `dismiss-recommendation-${finding.id}`
                                          }
                                          onClick={() =>
                                            dismissRecommendation(
                                              finding,
                                              recommendation
                                            )
                                          }
                                        >
                                          {recommendationActionKey ===
                                          `dismiss-recommendation-${finding.id}`
                                            ? "Dismissing..."
                                            : "Dismiss"}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : null}

                            {(() => {
                              const soarRun =
                                getFindingSoarRun(
                                  finding.id
                                );

                              if (!soarRun) {
                                return (
                                  <div className="threat-hunt-soar-launch">
                                    {(() => {
                                      const incidentRecommendations =
                                        recommendations[
                                          finding.incident_id
                                        ] || [];

                                      const activeRecommendations =
                                        incidentRecommendations.filter(
                                          (item) =>
                                            !item.dismissed
                                        );

                                      const recommendation =
                                        activeRecommendations[0];

                                      return (
                                        <>
                                          <div className="threat-hunt-recommendation-actions">
                                            <button
                                              type="button"
                                              className="threat-hunt-recommend-button"
                                              disabled={
                                                recommendationActionKey ===
                                                `recommend-${finding.id}`
                                              }
                                              onClick={() =>
                                                generateRecommendation(
                                                  finding
                                                )
                                              }
                                            >
                                              {recommendationActionKey ===
                                              `recommend-${finding.id}`
                                                ? "Analyzing..."
                                                : recommendation
                                                  ? "Refresh Recommendation"
                                                  : "Generate Recommendation"}
                                            </button>
                                          </div>

                                          {recommendation ? (
                                            <div className="threat-hunt-recommendation-card">
                                              <div className="threat-hunt-recommendation-header">
                                                <div>
                                                  <span>
                                                    Recommended Playbook
                                                  </span>

                                                  <strong>
                                                    {
                                                      recommendation.playbook_name
                                                    }
                                                  </strong>
                                                </div>

                                                <div className="threat-hunt-recommendation-confidence">
                                                  <strong>
                                                    {
                                                      recommendation.confidence
                                                    }
                                                    %
                                                  </strong>

                                                  <span>
                                                    Confidence
                                                  </span>
                                                </div>
                                              </div>

                                              {recommendation.playbook_description ? (
                                                <p>
                                                  {
                                                    recommendation.playbook_description
                                                  }
                                                </p>
                                              ) : null}

                                              <div className="threat-hunt-recommendation-meta">
                                                <span>
                                                  Rank #
                                                  {
                                                    recommendation.recommendation_rank
                                                  }
                                                </span>

                                                <span>
                                                  Score{" "}
                                                  {
                                                    recommendation.score
                                                  }
                                                </span>

                                                <span>
                                                  {
                                                    recommendation.rule_name ||
                                                    "Recommendation Rule"
                                                  }
                                                </span>
                                              </div>

                                              {Array.isArray(
                                                recommendation.recommendation_reasons
                                              ) &&
                                              recommendation.recommendation_reasons
                                                .length > 0 ? (
                                                <div className="threat-hunt-recommendation-reasons">
                                                  <span>
                                                    Matched signals
                                                  </span>

                                                  <div>
                                                    {recommendation.recommendation_reasons.map(
                                                      (
                                                        reason,
                                                        reasonIndex
                                                      ) => (
                                                        <small
                                                          key={
                                                            reasonIndex
                                                          }
                                                        >
                                                          {
                                                            reason.field
                                                          }
                                                          :{" "}
                                                          {
                                                            reason.actual
                                                          }
                                                          {" +"}
                                                          {
                                                            reason.weight
                                                          }
                                                        </small>
                                                      )
                                                    )}
                                                  </div>
                                                </div>
                                              ) : null}

                                              <div className="threat-hunt-recommendation-buttons">
                                                <button
                                                  type="button"
                                                  className="threat-hunt-recommended-run-button"
                                                  disabled={
                                                    recommendationActionKey ===
                                                    `run-recommendation-${finding.id}`
                                                  }
                                                  onClick={() =>
                                                    runRecommendedPlaybook(
                                                      finding,
                                                      recommendation
                                                    )
                                                  }
                                                >
                                                  {recommendationActionKey ===
                                                  `run-recommendation-${finding.id}`
                                                    ? "Creating Run..."
                                                    : "Run Recommended Playbook"}
                                                </button>

                                                <button
                                                  type="button"
                                                  className="threat-hunt-recommendation-dismiss-button"
                                                  disabled={
                                                    recommendationActionKey ===
                                                    `dismiss-recommendation-${finding.id}`
                                                  }
                                                  onClick={() =>
                                                    dismissRecommendation(
                                                      finding,
                                                      recommendation
                                                    )
                                                  }
                                                >
                                                  {recommendationActionKey ===
                                                  `dismiss-recommendation-${finding.id}`
                                                    ? "Dismissing..."
                                                    : "Dismiss"}
                                                </button>
                                              </div>
                                            </div>
                                          ) : null}
                                        </>
                                      );
                                    })()}

                                    <div className="threat-hunt-manual-playbook-label">
                                      Manual Playbook Selection
                                    </div>

                                    <select
                                      defaultValue=""
                                      id={`soar-playbook-${finding.id}`}
                                    >
                                      <option
                                        value=""
                                        disabled
                                      >
                                        Select SOAR Playbook
                                      </option>

                                      {soarPlaybooks.map(
                                        (playbook) => (
                                          <option
                                            key={
                                              playbook.id
                                            }
                                            value={
                                              playbook.id
                                            }
                                          >
                                            {
                                              playbook.name
                                            }
                                          </option>
                                        )
                                      )}
                                    </select>

                                    <button
                                      type="button"
                                      className="threat-hunt-soar-run-button"
                                      disabled={
                                        soarActionKey ===
                                        `create-${finding.id}`
                                      }
                                      onClick={() => {
                                        const select =
                                          document.getElementById(
                                            `soar-playbook-${finding.id}`
                                          );

                                        if (
                                          !select?.value
                                        ) {
                                          window.alert(
                                            "Select a SOAR playbook."
                                          );
                                          return;
                                        }

                                        runSoarPlaybook(
                                          finding,
                                          select.value
                                        );
                                      }}
                                    >
                                      {soarActionKey ===
                                      `create-${finding.id}`
                                        ? "Creating Run..."
                                        : "Run SOAR Playbook"}
                                    </button>
                                  </div>
                                );
                              }

                              const auditItems =
                                soarAudit[
                                  soarRun.soar_run_id
                                ] || [];

                              return (
                                <div className="threat-hunt-soar-run-card">

                                    <div className="threat-hunt-run-card-recommendation">
                                      {(() => {
                                        const incidentRecommendations =
                                          recommendations[
                                            finding.incident_id
                                          ] || [];

                                        const recommendation =
                                          incidentRecommendations.find(
                                            (item) =>
                                              !item.dismissed
                                          );

                                        if (!recommendation) {
                                          return (
                                            <button
                                              type="button"
                                              className="threat-hunt-recommend-button"
                                              disabled={
                                                recommendationActionKey ===
                                                `recommend-${finding.id}`
                                              }
                                              onClick={() =>
                                                generateRecommendation(
                                                  finding
                                                )
                                              }
                                            >
                                              {recommendationActionKey ===
                                              `recommend-${finding.id}`
                                                ? "Analyzing..."
                                                : "Generate Recommendation"}
                                            </button>
                                          );
                                        }

                                        return (
                                          <div className="threat-hunt-recommendation-card">
                                            <div className="threat-hunt-recommendation-header">
                                              <div>
                                                <span>
                                                  Recommended Playbook
                                                </span>

                                                <strong>
                                                  {
                                                    recommendation.playbook_name
                                                  }
                                                </strong>
                                              </div>

                                              <div className="threat-hunt-recommendation-confidence">
                                                <strong>
                                                  {
                                                    recommendation.confidence
                                                  }
                                                  %
                                                </strong>

                                                <span>
                                                  Confidence
                                                </span>
                                              </div>
                                            </div>

                                            {recommendation.playbook_description ? (
                                              <p>
                                                {
                                                  recommendation.playbook_description
                                                }
                                              </p>
                                            ) : null}

                                            <div className="threat-hunt-recommendation-meta">
                                              <span>
                                                Rank #
                                                {
                                                  recommendation.recommendation_rank
                                                }
                                              </span>

                                              <span>
                                                Score{" "}
                                                {
                                                  recommendation.score
                                                }
                                              </span>

                                              <span>
                                                {
                                                  recommendation.rule_name ||
                                                  "Recommendation Rule"
                                                }
                                              </span>
                                            </div>

                                            {Array.isArray(
                                              recommendation.recommendation_reasons
                                            ) &&
                                            recommendation.recommendation_reasons
                                              .length > 0 ? (
                                              <div className="threat-hunt-recommendation-reasons">
                                                <span>
                                                  Matched signals
                                                </span>

                                                <div>
                                                  {recommendation.recommendation_reasons.map(
                                                    (
                                                      reason,
                                                      reasonIndex
                                                    ) => (
                                                      <small
                                                        key={
                                                          reasonIndex
                                                        }
                                                      >
                                                        {
                                                          reason.field
                                                        }
                                                        :{" "}
                                                        {
                                                          reason.actual
                                                        }
                                                        {" +"}
                                                        {
                                                          reason.weight
                                                        }
                                                      </small>
                                                    )
                                                  )}
                                                </div>
                                              </div>
                                            ) : null}

                                            <div className="threat-hunt-recommendation-buttons">
                                              <span className="threat-hunt-recommendation-run-exists">
                                                SOAR run already linked
                                              </span>

                                              <button
                                                type="button"
                                                className="threat-hunt-recommend-button"
                                                disabled={
                                                  recommendationActionKey ===
                                                  `recommend-${finding.id}`
                                                }
                                                onClick={() =>
                                                  generateRecommendation(
                                                    finding
                                                  )
                                                }
                                              >
                                                {recommendationActionKey ===
                                                `recommend-${finding.id}`
                                                  ? "Analyzing..."
                                                  : "Refresh Recommendation"}
                                              </button>

                                              <button
                                                type="button"
                                                className="threat-hunt-recommendation-dismiss-button"
                                                disabled={
                                                  recommendationActionKey ===
                                                  `dismiss-recommendation-${finding.id}`
                                                }
                                                onClick={() =>
                                                  dismissRecommendation(
                                                    finding,
                                                    recommendation
                                                  )
                                                }
                                              >
                                                {recommendationActionKey ===
                                                `dismiss-recommendation-${finding.id}`
                                                  ? "Dismissing..."
                                                  : "Dismiss"}
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>

                                  <div className="threat-hunt-soar-run-header">
                                    <div>
                                      <span>
                                        SOAR Run #
                                        {
                                          soarRun.soar_run_id
                                        }
                                      </span>

                                      <strong>
                                        {
                                          soarRun.playbook_name
                                        }
                                      </strong>
                                    </div>

                                    <span
                                      className={statusClass(
                                        soarRun.run_status
                                      )}
                                    >
                                      {formatLabel(
                                        soarRun.run_status
                                      )}
                                    </span>
                                  </div>

                                  <div className="threat-hunt-soar-run-metrics">
                                    <div>
                                      <span>
                                        Current Step
                                      </span>

                                      <strong>
                                        {soarRun.current_step_order ||
                                          0}
                                      </strong>
                                    </div>

                                    <div>
                                      <span>
                                        Approval
                                      </span>

                                      <strong>
                                        {soarRun.approved
                                          ? "Approved"
                                          : soarRun.approval_required
                                          ? "Required"
                                          : "Not Required"}
                                      </strong>
                                    </div>
                                  </div>

                                  <div className="threat-hunt-soar-actions">
                                    {soarRun.approval_required &&
                                      !soarRun.approved &&
                                      ![
                                        "rejected",
                                        "completed",
                                        "failed",
                                        "cancelled",
                                      ].includes(
                                        soarRun.run_status
                                      ) && (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              performSoarAction(
                                                soarRun,
                                                "approve"
                                              )
                                            }
                                            disabled={
                                              soarActionKey ===
                                              `approve-${soarRun.soar_run_id}`
                                            }
                                          >
                                            {soarActionKey ===
                                            `approve-${soarRun.soar_run_id}`
                                              ? "Approving..."
                                              : "Approve"}
                                          </button>

                                          <button
                                            type="button"
                                            className="threat-hunt-soar-reject-button"
                                            onClick={() =>
                                              performSoarAction(
                                                soarRun,
                                                "reject"
                                              )
                                            }
                                            disabled={
                                              soarActionKey ===
                                              `reject-${soarRun.soar_run_id}`
                                            }
                                          >
                                            {soarActionKey ===
                                            `reject-${soarRun.soar_run_id}`
                                              ? "Rejecting..."
                                              : "Reject"}
                                          </button>
                                        </>
                                      )}

                                    {![
                                      "completed",
                                      "failed",
                                      "rejected",
                                      "cancelled",
                                    ].includes(
                                      soarRun.run_status
                                    ) && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          performSoarAction(
                                            soarRun,
                                            "sync"
                                          )
                                        }
                                        disabled={
                                          soarActionKey ===
                                          `sync-${soarRun.soar_run_id}`
                                        }
                                      >
                                        {soarActionKey ===
                                        `sync-${soarRun.soar_run_id}`
                                          ? "Syncing..."
                                          : "Sync Run"}
                                      </button>
                                    )}

                                    <button
                                      type="button"
                                      onClick={() =>
                                        viewSoarAudit(
                                          soarRun
                                        )
                                      }
                                      disabled={
                                        soarActionKey ===
                                        `audit-${soarRun.soar_run_id}`
                                      }
                                    >
                                      {soarActionKey ===
                                      `audit-${soarRun.soar_run_id}`
                                        ? "Loading..."
                                        : "View Audit"}
                                    </button>
                                  </div>

                                  {auditItems.length >
                                    0 && (
                                    <div className="threat-hunt-soar-audit">
                                      {auditItems.map(
                                        (
                                          audit,
                                          auditIndex
                                        ) => (
                                          <div
                                            key={
                                              audit.id ||
                                              auditIndex
                                            }
                                          >
                                            <strong>
                                              {formatLabel(
                                                audit.event_type
                                              )}
                                            </strong>

                                            <span>
                                              {
                                                audit.message
                                              }
                                            </span>

                                            <small>
                                              {formatDate(
                                                audit.created_at
                                              )}
                                            </small>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <button
                            type="button"
                            className="threat-hunt-escalate-button"
                            disabled={
                              escalatingFindingId ===
                              finding.id
                            }
                            onClick={() =>
                              escalateFinding(finding)
                            }
                          >
                            {escalatingFindingId ===
                            finding.id
                              ? "Escalating..."
                              : "Escalate to SOC"}
                          </button>
                        )}
                      </div>
                    </div>
                  )
                )
              ) : (
                <div className="threat-hunt-empty">
                  No findings created yet.
                </div>
              )}
            </article>
          </div>

          <div className="threat-hunt-intelligence-grid">
            <article className="threat-hunt-attack-panel">
              <div className="threat-hunt-panel-heading">
                <div>
                  <span>MITRE ATT&CK COVERAGE</span>
                  <h3>Tactics and Techniques</h3>
                </div>
              </div>

              <div className="threat-hunt-attack-group">
                <h4>Tactics</h4>

                <div className="threat-hunt-attack-tags">
                  {mitreCoverage.tactics.length > 0 ? (
                    mitreCoverage.tactics.map(
                      (tactic) => (
                        <span
                          key={tactic}
                          className="threat-hunt-attack-tactic"
                        >
                          {formatLabel(tactic)}
                        </span>
                      )
                    )
                  ) : (
                    <span className="threat-hunt-attack-empty">
                      No tactics mapped.
                    </span>
                  )}
                </div>
              </div>

              <div className="threat-hunt-attack-group">
                <h4>Techniques</h4>

                <div className="threat-hunt-attack-tags">
                  {mitreCoverage.techniques.length > 0 ? (
                    mitreCoverage.techniques.map(
                      (technique) => (
                        <span
                          key={technique}
                          className="threat-hunt-attack-technique"
                        >
                          {technique}
                        </span>
                      )
                    )
                  ) : (
                    <span className="threat-hunt-attack-empty">
                      No techniques mapped.
                    </span>
                  )}
                </div>
              </div>
            </article>

            <article className="threat-hunt-graph-panel">
              <div className="threat-hunt-panel-heading">
                <div>
                  <span>INVESTIGATION GRAPH</span>
                  <h3>Relationship Chain</h3>
                </div>
              </div>

              {investigationNodes.length > 0 ? (
                <div className="threat-hunt-graph">
                  {investigationNodes.map(
                    (node, index) => (
                      <React.Fragment key={node.key}>
                        <div
                          className={`threat-hunt-graph-node threat-hunt-graph-${node.key}`}
                        >
                          <span>{node.type}</span>
                          <strong>{node.label}</strong>
                        </div>

                        {index <
                          investigationNodes.length -
                            1 && (
                          <div className="threat-hunt-graph-connector">
                            <span>↓</span>
                          </div>
                        )}
                      </React.Fragment>
                    )
                  )}
                </div>
              ) : (
                <div className="threat-hunt-empty">
                  Run a hunt query to build the relationship graph.
                </div>
              )}
            </article>
          </div>

          <div className="threat-hunt-timeline-section">
            <div className="threat-hunt-panel-heading">
              <div>
                <span>INVESTIGATION HISTORY</span>
                <h3>Activity Timeline</h3>
              </div>
            </div>

            <div className="threat-hunt-timeline">
              {(selectedHunt.timeline || []).length > 0 ? (
                selectedHunt.timeline.map((item) => (
                  <article
                    className="threat-hunt-timeline-item"
                    key={item.id}
                  >
                    <div className="threat-hunt-timeline-marker" />

                    <div className="threat-hunt-timeline-content">
                      <div className="threat-hunt-timeline-heading">
                        <strong>
                          {formatLabel(item.event_type)}
                        </strong>

                        <span>
                          {formatDate(item.created_at)}
                        </span>
                      </div>

                      <p>{item.message}</p>

                      <small>
                        {item.actor_name || "Wireless Press"}
                        {item.actor_role
                          ? ` · ${formatLabel(item.actor_role)}`
                          : ""}
                      </small>
                    </div>
                  </article>
                ))
              ) : (
                <div className="threat-hunt-empty">
                  No investigation activity recorded yet.
                </div>
              )}
            </div>
          </div>

          {selectedEvidenceIds.length > 0 && (
            <div className="threat-hunt-evidence-toolbar">
              <div>
                <strong>
                  {selectedEvidenceIds.length}
                  {" "}evidence item(s) selected
                </strong>

                <span>
                  Convert selected evidence into a formal finding.
                </span>
              </div>

              <button
                type="button"
                onClick={() =>
                  setShowFindingForm(true)
                }
              >
                Create Finding
              </button>
            </div>
          )}

          {showFindingForm && (
            <form
              className="threat-hunt-finding-form"
              onSubmit={createFinding}
            >
              <div className="threat-hunt-panel-heading threat-hunt-detail-heading">
                <div>
                  <span>FORMAL ANALYST FINDING</span>
                  <h3>Create Finding</h3>
                </div>

                <button
                  type="button"
                  className="threat-hunt-close-button"
                  onClick={() =>
                    setShowFindingForm(false)
                  }
                >
                  Cancel
                </button>
              </div>

              <label className="threat-hunt-form-wide">
                Finding Title
                <input
                  required
                  value={findingForm.title}
                  onChange={(event) =>
                    setFindingForm({
                      ...findingForm,
                      title: event.target.value,
                    })
                  }
                />
              </label>

              <label className="threat-hunt-form-wide">
                Description
                <textarea
                  required
                  value={findingForm.description}
                  onChange={(event) =>
                    setFindingForm({
                      ...findingForm,
                      description:
                        event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Finding Type
                <select
                  value={findingForm.finding_type}
                  onChange={(event) =>
                    setFindingForm({
                      ...findingForm,
                      finding_type:
                        event.target.value,
                    })
                  }
                >
                  <option value="suspicious-activity">
                    Suspicious Activity
                  </option>
                  <option value="credential-compromise">
                    Credential Compromise
                  </option>
                  <option value="malware">
                    Malware
                  </option>
                  <option value="lateral-movement">
                    Lateral Movement
                  </option>
                  <option value="data-exfiltration">
                    Data Exfiltration
                  </option>
                </select>
              </label>

              <label>
                Severity
                <select
                  value={findingForm.severity}
                  onChange={(event) =>
                    setFindingForm({
                      ...findingForm,
                      severity:
                        event.target.value,
                    })
                  }
                >
                  <option value="critical">
                    Critical
                  </option>
                  <option value="high">
                    High
                  </option>
                  <option value="medium">
                    Medium
                  </option>
                  <option value="low">
                    Low
                  </option>
                </select>
              </label>

              <label>
                Confidence
                <input
                  type="number"
                  min="0"
                  max="100"
                  required
                  value={findingForm.confidence}
                  onChange={(event) =>
                    setFindingForm({
                      ...findingForm,
                      confidence:
                        event.target.value,
                    })
                  }
                />
              </label>

              <label>
                Assigned To
                <input
                  value={findingForm.assigned_to}
                  onChange={(event) =>
                    setFindingForm({
                      ...findingForm,
                      assigned_to:
                        event.target.value,
                    })
                  }
                />
              </label>

              <label>
                MITRE Tactic
                <input
                  placeholder="Initial Access"
                  value={findingForm.mitre_tactic}
                  onChange={(event) =>
                    setFindingForm({
                      ...findingForm,
                      mitre_tactic:
                        event.target.value,
                    })
                  }
                />
              </label>

              <label>
                MITRE Technique
                <input
                  placeholder="T1078"
                  value={findingForm.mitre_technique}
                  onChange={(event) =>
                    setFindingForm({
                      ...findingForm,
                      mitre_technique:
                        event.target.value,
                    })
                  }
                />
              </label>

              <label className="threat-hunt-form-wide">
                Recommended Actions
                <textarea
                  placeholder="Enter one action per line"
                  value={
                    findingForm.recommended_actions
                  }
                  onChange={(event) =>
                    setFindingForm({
                      ...findingForm,
                      recommended_actions:
                        event.target.value,
                    })
                  }
                />
              </label>

              <div className="threat-hunt-form-actions threat-hunt-form-wide">
                <button
                  type="submit"
                  disabled={creatingFinding}
                >
                  {creatingFinding
                    ? "Creating Finding..."
                    : "Create Finding"}
                </button>
              </div>
            </form>
          )}

          {selectedRunResults.length > 0 && (
            <div className="threat-hunt-results-section">
              <h4>Latest Query Results</h4>

              <div className="threat-hunt-table-wrapper">
                <table className="threat-hunt-table">
                  <thead>
                    <tr>
                      <th>Severity</th>
                      <th>Event</th>
                      <th>User</th>
                      <th>Source</th>
                      <th>Relevance</th>
                      <th>Disposition</th>
                      <th>Evidence</th>
                    </tr>
                  </thead>

                  <tbody>
                    {selectedRunResults.map(
                      (result) => (
                        <tr key={result.id}>
                          <td>
                            <span
                              className={severityClass(
                                result.severity
                              )}
                            >
                              {formatLabel(
                                result.severity
                              )}
                            </span>
                          </td>

                          <td>
                            <strong>
                              {formatLabel(
                                result.event_type
                              )}
                            </strong>

                            <span className="threat-hunt-subtext">
                              {formatDate(
                                result.event_time
                              )}
                            </span>
                          </td>

                          <td>
                            {result.username ||
                              "N/A"}
                          </td>

                          <td>
                            {formatLabel(
                              result.source_type
                            )}
                          </td>

                          <td>
                            {Number(
                              result.relevance_score ||
                                0
                            ).toFixed(1)}
                          </td>

                          <td>
                            <select
                              value={
                                result.analyst_disposition ||
                                "unreviewed"
                              }
                              onChange={(event) =>
                                reviewResult(
                                  result.id,
                                  event.target.value
                                )
                              }
                            >
                              <option value="unreviewed">
                                Unreviewed
                              </option>

                              <option value="benign">
                                Benign
                              </option>

                              <option value="suspicious">
                                Suspicious
                              </option>

                              <option value="malicious">
                                Malicious
                              </option>

                              <option value="false_positive">
                                False Positive
                              </option>
                            </select>
                          </td>

                          <td>
                            {preservedEvidence[result.id] ? (
                              <label className="threat-hunt-evidence-confirmed">
                                <input
                                  type="checkbox"
                                  checked={selectedEvidenceIds.includes(
                                    preservedEvidence[result.id].id
                                  )}
                                  onChange={() =>
                                    toggleEvidenceSelection(
                                      preservedEvidence[result.id].id
                                    )
                                  }
                                />

                                <div>
                                  <strong>
                                    Evidence #
                                    {preservedEvidence[result.id].id}
                                  </strong>
                                  <span>
                                    Select for finding
                                  </span>
                                </div>
                              </label>
                            ) : (
                              <button
                                type="button"
                                className="threat-hunt-evidence-button"
                                disabled={
                                  preservingResultId === result.id
                                }
                                onClick={() =>
                                  preserveEvidence(result)
                                }
                              >
                                {preservingResultId === result.id
                                  ? "Preserving..."
                                  : "Preserve Evidence"}
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}


export default ThreatHuntingPage;
